/**
 * Creation of the Patient Access call note.
 *
 * A scheduled call's card is created on demand — a rep clicks "Start intro call
 * note" in the Chart row of the appointment. Nothing creates it when the
 * appointment is booked: a card born at booking time invites a second, parallel
 * chart note, drifts away from its appointment when that appointment is
 * rescheduled, and is left behind empty when the call never happens.
 *
 * Lives apart from `patient-access-note.ts` (which owns parsing and previews)
 * because both the Orbit card and the inbound-call bot path build the resource
 * from here — the payload shape must not drift between them. The internal shape
 * indirection is what lets the intro call and the warm hand-off share it.
 */

import { MedplumClient, getReferenceString } from '@medplum/core';
import { Coding, Communication, Patient, Practitioner, Reference, Resource } from '@medplum/fhirtypes';
import {
  PAC_INTRO_CALL_NOTE_CATEGORY,
  PAC_WARM_HANDOFF_NOTE_CATEGORY,
  NORMAL_CONFIDENTIALITY_LEVEL_CODING,
} from './codings';
import {
  PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID,
  PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID,
  CommunicationCategory,
  TEXT_CONTENT_PAYLOAD_ID,
} from './constants';
import {
  isPnConsultationAnswers,
  PacIntroCallAnswers,
  PacVisitType,
  PN_CONSULTATION_VISIT_TYPE,
} from './patient-access-note';
import { EMPTY_WARM_HANDOFF_ANSWERS } from './patient-access-warm-handoff-note';

export interface CreatePatientAccessNoteParams {
  patient: Reference<Patient>;
  /**
   * The Appointment or inbound call this card documents, and its idempotency key.
   *
   * Optional because the hand-off card is started from the timeline's Add menu,
   * where there is no appointment to attach to. Given but unresolvable is still an
   * error — a caller that meant to attach the card must not silently get a
   * standalone one nothing can find again.
   */
  basedOn?: Reference<Resource>;
  /**
   * The Patient Access rep who will work the card.
   *
   * Optional: the inbound path creates a card before anyone has picked it up. The
   * write grant is clearance-scoped rather than `sender=%profile`, so a card with no
   * sender is still editable by whoever takes the call.
   */
  sender?: Reference<Practitioner>;
  /** ISO timestamp for the card's position on the timeline. */
  sent: string;
}

interface NoteShape {
  category: Coding;
  /** Search token for the conditional create, and for finding an existing card. */
  categoryCode: string;
  answersPayloadId: string;
  answers: unknown;
  /** Seeds the `text-content` payload so a generic renderer shows something. */
  summary: string;
}

/**
 * The search that identifies an existing card for a call.
 *
 * Shared by the conditional create below and by the Chart row, which uses it to
 * decide between offering "Start …" and linking to the card already there.
 */
export function patientAccessNoteSearchParams(basedOnReference: string, categoryCode: string): Record<string, string> {
  return { 'based-on': basedOnReference, category: categoryCode };
}

/**
 * The conditional-create key for a card with no appointment behind it.
 *
 * Scoped to the open card rather than to every card of this kind: a second
 * hand-off call after the first has been charted is a real second call and needs
 * its own card, while a double click on the Add menu must not produce two.
 *
 * `based-on:missing` is what keeps "no appointment behind it" true. Without it an
 * in-progress card started from an appointment or an inbound call matches here,
 * no standalone card is created, and the rep is focused into that call's script.
 */
export function openPatientAccessNoteSearchParams(
  patientReference: string,
  categoryCode: string
): Record<string, string> {
  return { subject: patientReference, category: categoryCode, status: 'in-progress', 'based-on:missing': 'true' };
}

/**
 * Creates the card for a call, if one does not already exist.
 *
 * Conditional-create on `based-on` is what keeps a double click, or a
 * re-delivered subscription on the inbound path, from putting two cards on the
 * patient's timeline. Note `MockClient` ignores the search query, so tests
 * cannot exercise that branch — see the testing notes in CLAUDE.md.
 */
async function createPatientAccessNote(
  medplum: MedplumClient,
  params: CreatePatientAccessNoteParams,
  shape: NoteShape
): Promise<Communication> {
  const { patient, basedOn, sender, sent } = params;

  const basedOnReference = basedOn ? getReferenceString(basedOn) : undefined;
  if (basedOn && !basedOnReference) {
    // A basedOn that cannot be resolved gives neither an idempotency key nor a way
    // to find the card again from the appointment it belongs to.
    throw new Error('A patient-access note needs a resolvable basedOn reference');
  }

  const patientReference = getReferenceString(patient);
  if (!patientReference) {
    // The standalone key rests on it, and a card with no subject reaches no timeline.
    throw new Error('A patient-access note needs a resolvable patient reference');
  }

  const note: Communication = {
    resourceType: 'Communication',
    status: 'in-progress',
    category: [{ coding: [shape.category] }],
    subject: patient,
    // `basedOn` is typed to Communication but carries the Appointment or call
    // this card documents.
    ...(basedOn ? { basedOn: [basedOn as Reference<Communication>] } : {}),
    sent,
    ...(sender ? { sender } : {}),
    payload: [
      { id: TEXT_CONTENT_PAYLOAD_ID, contentString: shape.summary },
      { id: shape.answersPayloadId, contentString: JSON.stringify(shape.answers) },
    ],
    meta: { security: [NORMAL_CONFIDENTIALITY_LEVEL_CODING] },
  };

  const query = new URLSearchParams(
    basedOnReference
      ? patientAccessNoteSearchParams(basedOnReference, shape.categoryCode)
      : openPatientAccessNoteSearchParams(patientReference, shape.categoryCode)
  ).toString();
  return medplum.createResourceIfNoneExist<Communication>(note, query);
}

export function buildEmptyAnswers(visitType: PacVisitType): PacIntroCallAnswers {
  return { visitType, values: {} };
}

export async function createCiIntroCallNote(
  medplum: MedplumClient,
  // `basedOn` is optional since the rep can start an inbound card from the chart-note
  // list, where there is no appointment or call record to attach it to. Without one
  // the create keys on the patient's open card instead.
  params: CreatePatientAccessNoteParams & { visitType: PacVisitType }
): Promise<Communication> {
  const answers = buildEmptyAnswers(params.visitType);
  return createPatientAccessNote(medplum, params, {
    category: PAC_INTRO_CALL_NOTE_CATEGORY,
    categoryCode: CommunicationCategory.PAC_INTRO_CALL_NOTE,
    answersPayloadId: PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID,
    answers,
    summary: isPnConsultationAnswers(answers) ? 'PN consultation' : 'Intro call',
  });
}

/**
 * The PN consultation card — the free 25-minute consultation a patient
 * self-books from the eligibility flow (Intake 1.1). The intro-call note on the
 * PN visit type; see `pn-consultation-note.ts` for why it is not its own category yet.
 */
export async function createPnConsultationNote(
  medplum: MedplumClient,
  params: CreatePatientAccessNoteParams
): Promise<Communication> {
  return createCiIntroCallNote(medplum, { ...params, visitType: PN_CONSULTATION_VISIT_TYPE });
}

/**
 * The hand-off card, started from the timeline's Add menu.
 *
 * No appointment behind it by design: the PAC joins the closing minutes of the NP
 * consult rather than booking a call of their own, and hanging the card off the
 * consult made the appointment decide which chart note a coordinator got. The rep
 * picks the note instead.
 *
 * No visit type either: the hand-off has one opening, so there is nothing to
 * branch on at creation time the way the intro call branches between Zoom and
 * inbound.
 */
export async function createWarmHandoffNote(
  medplum: MedplumClient,
  params: CreatePatientAccessNoteParams
): Promise<Communication> {
  return createPatientAccessNote(medplum, params, {
    category: PAC_WARM_HANDOFF_NOTE_CATEGORY,
    categoryCode: CommunicationCategory.PAC_WARM_HANDOFF_NOTE,
    answersPayloadId: PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID,
    answers: EMPTY_WARM_HANDOFF_ANSWERS,
    summary: 'Warm hand-off',
  });
}
